import React, { memo, useCallback, useEffect, useState } from "react";
import styles from "./LoanProductsPage.module.scss";
import TabSelector from "../../../common/components/tab-selector/TabSelector";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import solarProductService from "../../../httpService/SolarProductService";
import { NewSolarProduct } from "../../../model/solar-product/new-solar-product";
import { ExistingSolarProduct } from "../../../model/solar-product/existing-solar-product";
import { SolarProductStatus } from "../../../model/solar-product/solar-product-status";
import LoanOfferSideDialog, { LoanProductDialogSolarProduct } from "./loan-product-side-dialog/LoanProductSideDialog";
import PauseSolarProductDialog from "./pause-dialog/PauseSolarProductDialog";

type Props = {};

const LoanProductsPage: React.FC<Props> = memo((props: Props) => {
	const [selectedTab, setSelectedTab] = useState(1);

	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const pageSize = 6;

	const [loading, setLoading] = useState(false);

	const [newProducts, setNewProducts] = useState<NewSolarProduct[]>([]);
	const [existingProducts, setExistingProducts] = useState<ExistingSolarProduct[]>([]);

	const [showLoanOfferDialog, setShowLoanOfferDialog] = useState(false);
	const [solarProduct, setSolarProduct] = useState<LoanProductDialogSolarProduct | null>(null);

	const [showPauseDialog, setShowPauseDialog] = useState(false);
	const [pauseProduct, setPauseProduct] = useState<ExistingSolarProduct | null>(null);

	const statusStyle = (status: SolarProductStatus) => {
		switch (status) {
			case SolarProductStatus.ACTIVE:
				return styles.active;
			case SolarProductStatus.PAUSED:
				return styles.paused;
			case SolarProductStatus.ARCHIVED:
				return styles.archived;
			default:
				return "";
		}
	};

	const toggleAddNewLoanOfferDialog = () => {
		setShowLoanOfferDialog(!showLoanOfferDialog);
	};

	const toggleEditLoanOfferDialog = () => {
		setShowLoanOfferDialog(!showLoanOfferDialog);
	};

	const togglePauseDialog = () => {
		setShowPauseDialog(!showPauseDialog);
	};

	const addNewLoanOffer = (newSolarProduct: NewSolarProduct) => {
		setSolarProduct({
			id: newSolarProduct.id,
			productName: newSolarProduct.productName,
			cost: newSolarProduct.cost,
		});
		toggleAddNewLoanOfferDialog();
	};

	const editLoanOffer = (existingSolarProduct: ExistingSolarProduct) => {
		setSolarProduct({
			id: existingSolarProduct.id,
			productName: existingSolarProduct.productName,
			cost: existingSolarProduct.cost,
			loanProduct: existingSolarProduct.loanProduct,
		});
		toggleEditLoanOfferDialog();
	};

	const pauseSolarProduct = (existingSolarProduct: ExistingSolarProduct) => {
		setPauseProduct(existingSolarProduct);
		togglePauseDialog();
	};

	const fetchNewProducts = useCallback(async () => {
		try {
			setLoading(true);

			const response = await solarProductService.getNewPaged(currentPage, pageSize);

			const data = response.data;

			setNewProducts(data.items);

			setTotalPages(data.totalPages);
		} catch (error) {
		} finally {
			setLoading(false);
		}
	}, [currentPage]);

	const fetchExistingProducts = useCallback(async () => {
		try {
			setLoading(true);

			const response = await solarProductService.getExistingPaged(currentPage, pageSize);

			const data = response.data;

			setExistingProducts(data.items);

			setTotalPages(data.totalPages);
		} catch (error) {
		} finally {
			setLoading(false);
		}
	}, [currentPage]);

	const handleLoad = useCallback(() => {
		switch (selectedTab) {
			case 1:
				fetchNewProducts();
				break;
			case 2:
				fetchExistingProducts();
				break;
			default:
				break;
		}
	}, [selectedTab, fetchNewProducts, fetchExistingProducts]);

	useEffect(() => {
		handleLoad();
	}, [selectedTab, currentPage, handleLoad]);

	return (
		<>
			<div className={styles.pageTitle}>General Overview</div>
			<div className={styles.tabContainer}>
				<TabSelector
					selectedTab={selectedTab}
					tabs={[
						{
							id: 1,
							title: "New Solar Products",
							onClick: () => {
								setSelectedTab(1);
							},
						},
						{
							id: 2,
							title: "Existing Solar Products",
							onClick: () => {
								setSelectedTab(2);
							},
						},
					]}
				/>
			</div>
			{(() => {
				switch (selectedTab) {
					case 1:
						return (
							<div className={styles.tableContainer}>
								<table className="standardTable">
									<thead>
										<tr>
											<th>PRODUCT NAME</th>
											<th>PRODUCT COST</th>
											<th>SOLAR PROVIDER</th>
											<th>DATE ADDED</th>
											<th>PRODUCT ID</th>
											<th>ACTION</th>
										</tr>
									</thead>
									<tbody>
										{newProducts.map((product, index) => (
											<tr key={product.id}>
												<td>{product.productName}</td>
												<td>{product.cost.toLocaleString()}</td>
												<td>{product.solarProvider}</td>
												<td>{product.dateAdded.toLocaleDateString("en-GB")}</td>
												<td>{product.id}</td>
												<td>
													<button className={`standardButton ${styles.tableButton}`} onClick={() => addNewLoanOffer(product)}>
														Add Loan Offer
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
								<div className={styles.paginationContainer}>
									<CommonPagination
										currentPage={currentPage}
										isLoading={loading}
										numberOfPages={totalPages}
										onPageChange={(page: number) => {
											setCurrentPage(page);
										}}
									/>
								</div>
							</div>
						);
					case 2:
						return (
							<div className={styles.tableContainer}>
								<table className="standardTable">
									<thead>
										<tr>
											<th>PRODUCT NAME</th>
											<th>PRODUCT COST</th>
											<th>SOLAR PROVIDER</th>
											<th>MAX MONTH</th>
											<th>STATUS</th>
											<th>VIEWS</th>
											<th>SALES</th>
											<th>ACTION</th>
										</tr>
									</thead>
									<tbody>
										{existingProducts.map((product, index) => (
											<tr key={product.id}>
												<td>{product.productName}</td>
												<td>{product.cost.toLocaleString()}</td>
												<td>{product.solarProvider}</td>
												<td>{product.maxMonths}</td>
												<td className={`${styles.status} ${statusStyle(product.status)}`}>{product.status}</td>
												<td>{product.numberOfViews}</td>
												<td>{product.numberOfSales}</td>
												<td>
													<div style={{ display: "flex", gap: "10px" }}>
														<button className={`standardButton ${styles.tableButton}`} onClick={() => editLoanOffer(product)}>
															Edit
														</button>

														<button className={`standardButton ${styles.tableButton}`} onClick={() => pauseSolarProduct(product)}>
															{product.isPaused ? "Unpause" : "Pause"}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
								<div className={styles.paginationContainer}>
									<CommonPagination
										currentPage={currentPage}
										isLoading={loading}
										numberOfPages={totalPages}
										onPageChange={(page: number) => {
											setCurrentPage(page);
										}}
									/>
								</div>
							</div>
						);
					default:
						return <></>;
				}
			})()}
			{showLoanOfferDialog && (
				<LoanOfferSideDialog solarProduct={solarProduct!} onCloseDialog={toggleAddNewLoanOfferDialog} onSuccessfulExecution={handleLoad} />
			)}
			{showPauseDialog && <PauseSolarProductDialog product={pauseProduct!} onCloseDialog={togglePauseDialog} onProductPaused={handleLoad} />}
		</>
	);
});

export default LoanProductsPage;
